import React, {
  Dispatch,
  RefObject,
  SetStateAction,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import dayjs from 'dayjs';
import { deleteCartAndRemoveKeys } from '@helpers/cartFunctions';
import { store } from '@context/store';
import useSessionStorage from '@hooks/useSessionStorage';
import sessionStorageKeys from '@constants/sessionStorageKeys';
import DatePicker from 'react-datepicker';
import SearchOption from '@customTypes/search-option';
import { ProductDetails as ProductDetailsData } from '@customTypes/product-details';
import {
  getSmartCalendarAvailability,
  getSmartCalendarTimeGroupAvailability,
} from '@utils/services';
import { CartItemDetails } from '@customTypes/cart-items';
import { getDateWithDashes } from '@helpers/date';
import borderColors from '@constants/smartCalendar';
import { useCookies } from 'react-cookie';
import User from '@customTypes/user';
import Spinner from '@components/common/SmartCalendar/Spinner';
import ConfirmSection from '@components/common/SmartCalendar/ConfirmSection';
import DesktopCustomHeader from '@components/common/SmartCalendar/DesktopCustomHeader';
import { dlpSmartCalendarOpen, dlpSmartCalendarProductFilterApplied } from '@events/DLP';
import 'react-datepicker/dist/react-datepicker.css';
import actionTypes from '@context/actionTypes';
import Product from '@customTypes/product';
import { useRouter } from 'next/router';
import RoutingPath from '@constants/routingPath';
import { SELECT_SEARCH_ON_VIEW_DLP } from '@constants/amplitudeEvents';
import { useEvents } from '@events/EventsProvider';

type Props = {
  setConfirmedDate: (date: Date | undefined) => void;
  setShowDateWarning: Dispatch<SetStateAction<boolean>>;
  setBorderColor: Dispatch<SetStateAction<string>>;
  productDetails: ProductDetailsData | undefined;
  hotelId: number | undefined;
  confirmedDate: Date | undefined;
  productsRef: RefObject<HTMLDivElement> | null;
  setCartData: React.Dispatch<React.SetStateAction<CartItemDetails | undefined>>;
};

export default function SmartCalendar({
  setConfirmedDate,
  confirmedDate,
  productDetails,
  hotelId,
  setShowDateWarning,
  setBorderColor,
  productsRef,
  setCartData,
}: Props) {
  const [localDate, setLocalDate] = useState<Date | undefined>();
  const [dropdownValue, setDropdownValue] = useState<SearchOption>();
  const [dateAvailability, setDateAvailability] = useState<Date[]>([]);
  const [isSpinnerLoading, setIsSpinnerLoading] = useState<boolean>(false);
  const { getItem } = useSessionStorage();
  const cartId = getItem(sessionStorageKeys.CART_ID);
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { smartCalendarProductFilter } = state;

  const router = useRouter();
  const { track } = useEvents();
  const [cookies] = useCookies();
  const user: User = cookies.userInformation;

  const options: SearchOption[] = [];
  const containerRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<any>();
  const closePickerTimeoutRef = useRef<any>();
  const confirmedDateTimeoutRef = useRef<any>();
  const coachMarkTimeoutRef = useRef<any>();
  const scrollIntoViewTimeout = useRef<any>();

  // Setting options for dropdown
  productDetails?.products.forEach((product: Product) => {
    options.push({
      label: product.name,
      value: product.product_time_group_id || product.product_id.toString(),
      hideOtherOptions: false,
    });
  });

  const onConfirmClick = () => {
    if (cartId) {
      deleteCartAndRemoveKeys(cartId, dispatch, user);
      setCartData(undefined);
    }

    if (datePickerRef && datePickerRef.current && datePickerRef.current.isCalendarOpen()) {
      datePickerRef.current.setOpen(false);
      setConfirmedDate(localDate);
      setShowDateWarning(false);
    }

    if (router.pathname.includes(RoutingPath.DLP) && localDate) {
      track(SELECT_SEARCH_ON_VIEW_DLP, {
        hotel_id: hotelId,
        search_date: getDateWithDashes(localDate),
      });
    }
  };

  const getData = useCallback(
    async (value: any) => {
      const unavailableDatesArray: Date[] = [];
      const from = dayjs().format('YYYY-MM-DD');
      const to = dayjs().add(6, 'month').format('YYYY-MM-DD');

      if (hotelId) {
        try {
          setIsSpinnerLoading(true);

          // Extract the selected product from the products array.
          const product = productDetails?.products.find((productData: Product) => {
            if (productData.is_product_time_group) {
              return productData.product_time_group_id === value.value;
            }

            return productData.product_id === +value.value;
          });

          const availability = product?.is_product_time_group
            ? await getSmartCalendarTimeGroupAvailability(hotelId, value.value, from, to)
            : await getSmartCalendarAvailability(hotelId, value.value, from, to);

          setIsSpinnerLoading(false);

          if (availability) {
            availability.product_inventory?.forEach((item: { date: string; quantity: number }) => {
              if (item.quantity === 0) {
                unavailableDatesArray.push(new Date(`${item.date}T15:00:00.000Z`));
              }
            });

            // This contains the unavailable dates only.
            setDateAvailability(unavailableDatesArray);
          }
        } catch (error) {
          //
        } finally {
          setIsSpinnerLoading(false);
        }
      }
    },

    [hotelId, productDetails],
  );

  const onNextMonthClick = (increaseMonth: () => void) => {
    increaseMonth();
  };

  const onPreviousMonthClick = (decreaseMonth: () => void) => {
    decreaseMonth();
  };

  const onOptionSelect = useCallback(
    async (newValue: SearchOption) => {
      setLocalDate(undefined);
      setBorderColor(borderColors.DEFAULT);
      setDropdownValue(newValue);
      getData(newValue);

      // Event
      dlpSmartCalendarProductFilterApplied(hotelId || null, newValue.label);
    },
    [setBorderColor, getData, hotelId],
  );
  const onDateChange = (date: Date, e: SyntheticEvent) => {
    if (e) {
      e.preventDefault();
    }
    setLocalDate(date);
    if (date) {
      setBorderColor(borderColors.BLUE);
    }
  };

  const resetAll = () => {
    dispatch({
      type: actionTypes.SET_SMART_CALENDAR_PRODUCT_FILTER,
      payload: null,
    });
    setDropdownValue(undefined);
    setDateAvailability([]);
  };

  const onCalendarOpen = () => {
    if (localDate) {
      setBorderColor(borderColors.BLUE);
    }
    if (productsRef?.current) {
      productsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Event
    dlpSmartCalendarOpen();
  };

  const onCalendarClose = () => {
    if (datePickerRef && datePickerRef.current) {
      setLocalDate(confirmedDate);
    }
    setBorderColor(borderColors.DEFAULT);
    dispatch({
      type: actionTypes.SET_SMART_CALENDAR_PRODUCT_FILTER,
      payload: null,
    });
    setDropdownValue(undefined);
    setDateAvailability([]);
    dispatch({ type: actionTypes.OPEN_DATE_PICKER, payload: false });
  };

  // Get the filter from the global state.
  useEffect(() => {
    if (smartCalendarProductFilter) {
      onOptionSelect(smartCalendarProductFilter);
    }
  }, [onOptionSelect, smartCalendarProductFilter]);

  // Clearing all the timeouts on unmount
  useEffect(() => () => clearTimeout(closePickerTimeoutRef.current), []);
  useEffect(() => () => clearTimeout(confirmedDateTimeoutRef.current), []);
  useEffect(() => () => clearTimeout(coachMarkTimeoutRef.current), []);
  useEffect(() => () => clearTimeout(scrollIntoViewTimeout.current), []);

  useEffect(() => {
    if (confirmedDate) {
      setLocalDate(confirmedDate);
    }
  }, [confirmedDate]);

  return (
    <div className="desktop w-full" ref={containerRef}>
      <DatePicker
        ref={datePickerRef}
        preventOpenOnFocus
        onFocus={(e) => e.target.blur()}
        selected={localDate}
        onChange={onDateChange}
        minDate={new Date()}
        maxDate={dayjs().add(6, 'month').toDate()}
        shouldCloseOnSelect={false}
        highlightDates={[{ 'cross-dates': dateAvailability }]}
        fixedHeight
        dateFormat="MMM d, yyyy"
        placeholderText="mm/dd/yyyy"
        popperPlacement="bottom"
        onCalendarOpen={onCalendarOpen}
        onCalendarClose={onCalendarClose}
        wrapperClassName="w-full"
        renderCustomHeader={({
          date,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <DesktopCustomHeader
            options={options}
            onOptionSelect={onOptionSelect}
            onDropDownReset={resetAll}
            dropdownValue={dropdownValue}
            decreaseMonth={decreaseMonth}
            increaseMonth={increaseMonth}
            onPreviousMonthClick={onPreviousMonthClick}
            onNextMonthClick={onNextMonthClick}
            prevMonthButtonDisabled={prevMonthButtonDisabled}
            nextMonthButtonDisabled={nextMonthButtonDisabled}
            date={date}
          />
        )}
      >
        {isSpinnerLoading && <Spinner />}

        <ConfirmSection localDate={localDate} onConfirmClick={onConfirmClick} />
      </DatePicker>
    </div>
  );
}
